import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ResponsiveSearchPanel } from "@/components/Responsive";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MIN_TAP_TARGET, MOBILE_BREAKPOINT } from "@/styles/responsive";
import { summarize } from "./rows";
import { filterRows, statusFilters, type StatusFilter } from "./rowView";
import { downloadCorporationCsv } from "./csvExport";
import { ResultsTable } from "./ResultsTable";
import { type CorporationListState } from "./useCorporationList";
import { buttonSx, mock, Panel } from "./Panel";

interface Props {
  state: CorporationListState;
  disabled: boolean;
  acquisitionEnabled: boolean;
  onBack: () => void;
  onResolve: (rowNumber: number) => void;
  onOpenConfirm: () => void;
}

// モックの集計カードの配色。取得対象は主色、番号付与済みは成功、要確認は注意、エラーは危険
const metricColors: Record<string, string> = {
  取得対象: mock.tealDark,
  番号付与済み: "#247b5a",
  要確認: "#bd741f",
  エラー: "#cf4c4c",
};
// モックの .search と同じ高さ34px・角丸3px
const fieldSx = {
  "& .MuiOutlinedInput-root": { height: 34, borderRadius: "3px", fontSize: 14 },
};
const metricLabels: Record<string, string> = { 取込: "取込件数" };

/** ③ 法人の確認。集計・絞り込み・行選択による一括操作・CSV出力を行い、確定した行だけを取得へ進める */
export const ConfirmStep = ({
  state,
  disabled,
  acquisitionEnabled,
  onBack,
  onResolve,
  onOpenConfirm,
}: Props): JSX.Element => {
  // 検索条件は入力中の値と適用済みの値を分ける。SPのダイアログは閉じると中身が破棄されるため親で持つ
  const [draft, setDraft] = useState<{ filter: StatusFilter; keyword: string }>(
    { filter: "ALL", keyword: "" }
  );
  const [applied, setApplied] = useState(draft);
  const isMobile = useIsMobile();
  // SPではタップ領域を44px以上にする（レスポンシブ基盤ガイド）
  const tapSx = isMobile ? { minHeight: MIN_TAP_TARGET } : {};
  const [selected, setSelected] = useState<number[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const inputs = new Map(state.inputs.map((input) => [input.rowNumber, input]));
  const rows = filterRows(state.rows, inputs, applied.filter, applied.keyword);
  const visibleRowNumbers = new Set(rows.map((row) => row.rowNumber));
  const visibleSelected = selected.filter((rowNumber) =>
    visibleRowNumbers.has(rowNumber)
  );
  useEffect(() => {
    if (visibleSelected.length !== selected.length)
      setSelected(visibleSelected);
  }, [rows, selected]);
  const count = state.rows.filter((row) => row.targetState === "TARGET").length;
  // 除外や絞り込みで行が減って最終ページがなくなっても、残っている最後のページを表示する
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const bulkExclude = async (explicitlyExcluded: boolean): Promise<void> => {
    if (await state.changeRows(visibleSelected, { explicitlyExcluded }))
      setSelected([]);
  };

  const search = (): boolean => {
    setApplied(draft);
    setSelected([]);
    setPage(0);
    return true;
  };
  const clear = (): void => {
    const cleared = { filter: "ALL" as StatusFilter, keyword: "" };
    setDraft(cleared);
    setApplied(cleared);
    setSelected([]);
    setPage(0);
  };
  const filterFields = (
    <>
      <TextField
        select
        // SPは標準の大きさ（56px）にしてタップ領域を確保する。PCはモックの34pxに合わせる
        size={isMobile ? "medium" : "small"}
        value={draft.filter}
        SelectProps={{ SelectDisplayProps: { "aria-label": "ステータス" } }}
        onChange={(event) => {
          setDraft({ ...draft, filter: event.target.value as StatusFilter });
        }}
        sx={{
          width: { xs: "100%", [MOBILE_BREAKPOINT]: 190 },
          ...(isMobile ? {} : fieldSx),
        }}
      >
        {Object.entries(statusFilters).map(([value, label]) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size={isMobile ? "medium" : "small"}
        placeholder="法人名で検索"
        inputProps={{ "aria-label": "法人名で検索" }}
        value={draft.keyword}
        onChange={(event) => {
          setDraft({ ...draft, keyword: event.target.value });
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") search();
        }}
        sx={{
          width: { xs: "100%", [MOBILE_BREAKPOINT]: 220 },
          ...(isMobile ? {} : fieldSx),
        }}
      />
    </>
  );

  return (
    <Stack spacing="20px">
      {state.matchingRequired && (
        <Alert severity={state.busy ? "info" : "warning"}>
          {state.busy
            ? "法人を再照合しています。完了するまで前回の照合結果を表示します。"
            : "前回の照合結果を表示しています。修正・除外の内容を反映するには、全件を再照合してください。照合が完了するまで取得を依頼できません。"}
        </Alert>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            [MOBILE_BREAKPOINT]: "repeat(3, 1fr)",
            lg: "repeat(6, minmax(104px, 1fr))",
          },
          gap: "10px",
        }}
      >
        {summarize(state.rows).map(([label, value]) => (
          <Box
            key={label}
            sx={{
              border: `1px solid ${mock.line}`,
              borderRadius: "3px",
              px: "14px",
              py: "13px",
              bgcolor: "#fff",
            }}
          >
            <Typography sx={{ color: "#727a7e", fontSize: 11, mb: "7px" }}>
              {metricLabels[label] ?? label}
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: 21,
                lineHeight: 1.2,
                color: metricColors[label] ?? mock.ink,
              }}
            >
              {value.toLocaleString()}
            </Typography>
          </Box>
        ))}
      </Box>
      <Panel
        title="法人の確認"
        note="要確認・エラーの行は、法人を特定するか除外するまで取得依頼に含まれません。確度は一致条件による判定です。"
        actions={
          <Stack direction="row" gap={1} flexWrap="wrap">
            <Button
              variant="outlined"
              sx={[buttonSx.base, buttonSx.secondary, buttonSx.small, tapSx]}
              disabled={disabled || state.matchingRequired}
              onClick={() => {
                downloadCorporationCsv(state.rows, state.inputs);
              }}
            >
              CSV出力
            </Button>
            <Button
              variant="outlined"
              sx={[buttonSx.base, buttonSx.outline, buttonSx.small, tapSx]}
              disabled={
                disabled ||
                (!state.matchingRequired &&
                  !state.rows.some(
                    (row) =>
                      row.targetState === "HELD" && !row.explicitlyExcluded
                  ))
              }
              onClick={() => {
                setSelected([]);
                void state.retryMatching();
              }}
            >
              {state.matchingRequired ? "全件を再照合" : "未特定行を再突合"}
            </Button>
          </Stack>
        }
        bodySx={{
          px: { xs: 2, [MOBILE_BREAKPOINT]: "20px" },
          pt: "20px",
          pb: 0,
        }}
        footer={
          <>
            <Typography sx={{ fontSize: 14 }}>
              確認済みの取得対象
              <Box
                component="strong"
                sx={{ color: mock.tealDark, fontSize: 18, mx: "4px" }}
              >
                {count.toLocaleString()}
              </Box>
              件
            </Typography>
            {/* 幅320pxでは2つのボタンが1行に収まらないため折り返す */}
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Button
                variant="outlined"
                disabled={state.busy || state.sending}
                onClick={onBack}
                sx={[buttonSx.base, buttonSx.secondary, tapSx]}
              >
                戻る
              </Button>
              <Button
                variant="contained"
                disabled={
                  disabled ||
                  state.matchingRequired ||
                  !count ||
                  !acquisitionEnabled
                }
                onClick={onOpenConfirm}
                sx={[buttonSx.base, buttonSx.primary, tapSx]}
              >
                商業登記の取得内容を設定
              </Button>
            </Stack>
          </>
        }
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap="12px"
          sx={{ mb: "13px" }}
        >
          <Stack direction="row" gap={1} flexWrap="wrap">
            <Button
              variant="outlined"
              sx={[buttonSx.base, buttonSx.secondary, buttonSx.small, tapSx]}
              disabled={
                disabled ||
                state.matchingRequired ||
                visibleSelected.length === 0
              }
              onClick={() => {
                void bulkExclude(false);
              }}
            >
              選択行を取得対象にする
            </Button>
            <Button
              variant="outlined"
              sx={[buttonSx.base, buttonSx.secondary, buttonSx.small, tapSx]}
              disabled={
                disabled ||
                state.matchingRequired ||
                visibleSelected.length === 0
              }
              onClick={() => {
                void bulkExclude(true);
              }}
            >
              選択行を除外
            </Button>
          </Stack>
          {isMobile ? (
            <ResponsiveSearchPanel
              onSearch={search}
              onClear={clear}
              activeCount={
                (applied.filter === "ALL" ? 0 : 1) +
                (applied.keyword.trim() ? 1 : 0)
              }
            >
              <Stack gap={2}>{filterFields}</Stack>
            </ResponsiveSearchPanel>
          ) : (
            // PCの ResponsiveSearchPanel は枠付きの大きな検索欄になり、モックの1行のツールバーに収まらないため、PCだけ同じ入力欄を横に並べる
            <Stack direction="row" gap={1} alignItems="center">
              {filterFields}
              <Button
                variant="outlined"
                sx={[buttonSx.base, buttonSx.secondary, buttonSx.small]}
                onClick={clear}
              >
                クリア
              </Button>
              <Button
                variant="contained"
                sx={[
                  buttonSx.base,
                  buttonSx.primary,
                  buttonSx.small,
                  // 主ボタンの最小幅220pxはPCのメディアクエリで効くため、同じブレークポイントで打ち消す
                  { minWidth: { xs: 0, [MOBILE_BREAKPOINT]: 0 } },
                ]}
                onClick={search}
              >
                検索
              </Button>
            </Stack>
          )}
        </Stack>
        <ResultsTable
          rows={rows}
          inputs={inputs}
          selected={visibleSelected}
          onSelectedChange={setSelected}
          page={currentPage}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(0);
          }}
          disabled={disabled}
          resolve={onResolve}
        />
      </Panel>
    </Stack>
  );
};
